import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, ExternalLink,
  Instagram, Facebook, Music2, MessageCircle, Mail, Server, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const PLATFORM_META = {
  instagram: { label: "Instagram", color: "#E1306C", icon: Instagram },
  facebook: { label: "Facebook", color: "#1877F2", icon: Facebook },
  tiktok: { label: "TikTok", color: "#1A1D1B", icon: Music2 },
  whatsapp: { label: "WhatsApp", color: "#25D366", icon: MessageCircle },
  email: { label: "Email", color: "#00A36C", icon: Mail },
};

const STATUS_LABELS = {
  draft: "Borrador",
  scheduled: "Programada",
  published: "Publicada",
  failed: "Fallida",
  sending: "Enviando",
  sent: "Enviada",
};

const STATUS_STYLES = {
  draft: "bg-stone-100 text-stone-700 border-stone-200",
  scheduled: "bg-brand-gold-soft text-brand-gold-dark border-brand-gold/40",
  published: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30",
  sent: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30",
  sending: "bg-blue-50 text-blue-700 border-blue-200",
  failed: "bg-destructive/10 text-destructive border-destructive/30",
};

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function monthGrid(year, month) {
  // month = 0-indexed. Return array of dates covering full weeks (Mon-Sun).
  const first = new Date(year, month, 1);
  const dayOfWeek = (first.getDay() + 6) % 7; // Monday = 0
  const start = new Date(year, month, 1 - dayOfWeek);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function eventDate(evt) {
  return evt.at ? new Date(evt.at) : null;
}

export default function GlobalCalendar() {
  const { isMaster } = useAuth();
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platformFilter, setPlatformFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [schedInfo, setSchedInfo] = useState(null);

  const monthStart = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth(), 1), [cursor]);
  const monthEnd = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59), [cursor]);
  const days = useMemo(() => monthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { start: monthStart.toISOString(), end: monthEnd.toISOString() };
      if (platformFilter !== "all") params.platform = platformFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/calendar/events", { params });
      setEvents(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar el calendario");
    } finally {
      setLoading(false);
    }
  };

  const loadSchedulerStatus = async () => {
    if (!isMaster) return;
    try {
      const { data } = await api.get("/calendar/scheduler-status");
      setSchedInfo(data);
    } catch (_) {
      // ignore
    }
  };

  useEffect(() => {
    load();
    loadSchedulerStatus();
  }, [monthStart.getTime(), platformFilter, statusFilter]);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const e of events) {
      const d = eventDate(e);
      if (!d) continue;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    return map;
  }, [events]);

  const today = new Date();

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="global-calendar-page">
      <div>
        <div className="divider-gold mb-3" />
        <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
          <CalendarIcon className="h-8 w-8 text-brand-emerald" />
          Calendario Global
        </h1>
        <p className="text-sm text-stone-500 mt-2">
          Vista unificada de todas sus publicaciones sociales y campañas de correo.
          Solo se muestran eventos de los módulos que tiene habilitados.
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="font-serif-display text-2xl text-brand-ink font-semibold min-w-[220px] text-center">
            {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} data-testid="cal-today">
            Hoy
          </Button>
        </div>
        <div className="flex gap-2 items-center">
          <div className="w-40">
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger data-testid="cal-platform-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas plataformas</SelectItem>
                {Object.entries(PLATFORM_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="cal-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Scheduler status (Master) */}
      {isMaster && schedInfo && (
        <div className="card-elevated p-4 flex items-center gap-4 flex-wrap" data-testid="scheduler-status">
          <div className="flex items-center gap-2 text-brand-emerald-dark">
            <Server className="h-4 w-4" />
            <span className="font-semibold text-sm">Scheduler</span>
          </div>
          <div className="text-xs text-stone-500">
            Intervalo: <strong>{schedInfo.interval_seconds}s</strong> · Procesadores: <strong>{schedInfo.processor_count}</strong>
          </div>
          <div className="text-xs text-stone-500">
            Posts programados: <strong>{schedInfo.posts_scheduled}</strong> ({schedInfo.posts_overdue} vencidos)
          </div>
          <div className="text-xs text-stone-500">
            Campañas programadas: <strong>{schedInfo.campaigns_scheduled}</strong> ({schedInfo.campaigns_overdue} vencidas)
          </div>
        </div>
      )}

      {loading ? (
        <div className="card-elevated p-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
      ) : (
        <>
        {/* Desktop / tablet: vista de calendario en cuadrícula */}
        <div className="hidden md:block card-elevated overflow-hidden">
          {/* Weekday header */}
          <div className="grid grid-cols-7 border-b border-border bg-stone-50/60">
            {WEEKDAYS.map((w) => (
              <div key={w} className="px-3 py-2 text-xs font-semibold text-stone-500 uppercase tracking-widest text-center">{w}</div>
            ))}
          </div>
          {/* Grid */}
          <div className="grid grid-cols-7 auto-rows-[minmax(120px,auto)]">
            {days.map((d, idx) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = sameDay(d, today);
              const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
              const dayEvents = eventsByDay.get(key) || [];
              return (
                <div
                  key={idx}
                  data-testid={`cal-cell-${d.toISOString().slice(0, 10)}`}
                  className={`p-2 border-b border-r border-border ${inMonth ? "bg-white" : "bg-stone-50/30"} ${isToday ? "ring-2 ring-inset ring-brand-gold" : ""}`}
                >
                  <div className={`text-xs font-semibold ${inMonth ? "text-brand-ink" : "text-stone-300"} mb-1`}>
                    {d.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((e) => {
                      const meta = PLATFORM_META[e.platform] || PLATFORM_META.email;
                      const Icon = meta.icon;
                      return (
                        <button
                          key={e.id}
                          onClick={() => setSelectedEvent(e)}
                          className="w-full text-left rounded px-1.5 py-1 text-[11px] leading-tight hover:opacity-80 transition-opacity flex items-center gap-1"
                          style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
                          data-testid={`cal-event-${e.id}`}
                        >
                          <Icon className="h-3 w-3 shrink-0" />
                          <span className="truncate flex-1">
                            {eventDate(e)?.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })} · {e.title || "(sin título)"}
                          </span>
                        </button>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <button
                        onClick={() => setSelectedEvent({ __day: d, __events: dayEvents })}
                        className="text-[10px] text-stone-500 hover:text-brand-emerald-dark"
                      >
                        +{dayEvents.length - 3} más
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Móvil: vista de agenda (lista) */}
        <div className="md:hidden space-y-3" data-testid="cal-agenda-mobile">
          {(() => {
            const monthDays = days.filter((d) => d.getMonth() === cursor.getMonth());
            const withEvents = monthDays.filter((d) => {
              const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
              return (eventsByDay.get(key) || []).length > 0;
            });
            if (withEvents.length === 0) {
              return (
                <div className="card-elevated p-10 text-center text-stone-500 text-sm">
                  Sin eventos este mes.
                </div>
              );
            }
            return withEvents.map((d) => {
              const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
              const dayEvents = eventsByDay.get(key) || [];
              const isToday = sameDay(d, today);
              return (
                <div key={key} className="card-elevated overflow-hidden">
                  <div className={`px-4 py-2 border-b border-border flex items-center gap-2 ${isToday ? "bg-brand-gold-soft/50" : "bg-stone-50/60"}`}>
                    <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">
                      {d.toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "short" })}
                    </div>
                    {isToday && <Badge className="bg-brand-gold text-brand-ink border-brand-gold-dark text-[10px]">Hoy</Badge>}
                  </div>
                  <div className="divide-y divide-border">
                    {dayEvents.map((e) => {
                      const meta = PLATFORM_META[e.platform] || PLATFORM_META.email;
                      const Icon = meta.icon;
                      return (
                        <button
                          key={e.id}
                          onClick={() => setSelectedEvent(e)}
                          className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-stone-50 transition-colors"
                          data-testid={`cal-agenda-event-${e.id}`}
                        >
                          <div className="h-9 w-9 rounded-md flex items-center justify-center text-white shrink-0" style={{ backgroundColor: meta.color }}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-brand-ink line-clamp-1">{e.title || "(sin título)"}</div>
                            <div className="text-xs text-stone-500">
                              {eventDate(e)?.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })} · {meta.label}
                            </div>
                          </div>
                          <Badge className={`border font-semibold text-[10px] ${STATUS_STYLES[e.status] || ""}`}>
                            {STATUS_LABELS[e.status] || e.status}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
        </>
      )}

      {/* Event detail dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={(v) => !v && setSelectedEvent(null)}>
        <DialogContent data-testid="event-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {selectedEvent?.__events
                ? `Eventos del ${selectedEvent.__day?.toLocaleDateString("es-ES", { dateStyle: "long" })}`
                : "Detalle del evento"}
            </DialogTitle>
            <DialogDescription>
              {selectedEvent?.__events
                ? `${selectedEvent.__events.length} eventos programados este día.`
                : "Información completa del evento y enlace directo al módulo."}
            </DialogDescription>
          </DialogHeader>
          {selectedEvent?.__events ? (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {selectedEvent.__events.map((e) => <EventRow key={e.id} evt={e} />)}
            </div>
          ) : selectedEvent && (
            <EventDetail evt={selectedEvent} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EventRow({ evt }) {
  const meta = PLATFORM_META[evt.platform] || PLATFORM_META.email;
  const Icon = meta.icon;
  return (
    <div className="rounded-lg border border-border p-3 flex items-center gap-3">
      <div className="h-9 w-9 rounded-md flex items-center justify-center text-white shrink-0" style={{ backgroundColor: meta.color }}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-brand-ink line-clamp-1 font-medium">{evt.title || evt.name}</div>
        <div className="text-xs text-stone-500 flex items-center gap-2">
          <Badge className={`border font-semibold ${STATUS_STYLES[evt.status] || ""}`}>
            {STATUS_LABELS[evt.status] || evt.status}
          </Badge>
          {evt.at && <span>{new Date(evt.at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</span>}
          {evt.author_name && <span>· {evt.author_name}</span>}
        </div>
      </div>
      <Link to={evt.target_url}>
        <Button size="sm" variant="ghost" className="text-brand-emerald-dark">
          <ExternalLink className="h-4 w-4" />
        </Button>
      </Link>
    </div>
  );
}

function EventDetail({ evt }) {
  const meta = PLATFORM_META[evt.platform] || PLATFORM_META.email;
  const Icon = meta.icon;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: meta.color }}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="font-semibold text-brand-ink">{meta.label}</div>
          <Badge className={`border font-semibold ${STATUS_STYLES[evt.status] || ""}`}>
            {STATUS_LABELS[evt.status] || evt.status}
          </Badge>
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Contenido / Asunto</div>
        <p className="text-sm text-brand-ink whitespace-pre-wrap">{evt.title || evt.name || "—"}</p>
      </div>
      {evt.author_name && (
        <div className="text-xs text-stone-500">Autor: <strong>{evt.author_name}</strong></div>
      )}
      {evt.connection_handle && (
        <div className="text-xs text-stone-500">Cuenta: <strong>{evt.connection_handle}</strong></div>
      )}
      {evt.list_name && (
        <div className="text-xs text-stone-500">Lista: <strong>{evt.list_name}</strong></div>
      )}
      {evt.stats && (
        <div className="text-xs text-stone-500">
          Enviados: <strong>{evt.stats.sent}</strong> · Abiertos: <strong>{evt.stats.opened}</strong>
        </div>
      )}
      {evt.at && (
        <div className="text-xs text-stone-500">Fecha: {new Date(evt.at).toLocaleString("es-ES", { dateStyle: "long", timeStyle: "short" })}</div>
      )}
      <div className="pt-3">
        <Link to={evt.target_url}>
          <Button className="bg-brand-emerald text-white hover:bg-brand-emerald-dark" data-testid="event-goto">
            <ExternalLink className="h-4 w-4 mr-2" /> Ir al módulo
          </Button>
        </Link>
      </div>
    </div>
  );
}
